import { AuthService } from 'src/app/services/auth.service';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChange, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';
import { OffersService } from '../../../services/resources/offers.service';
import { ExtendService } from 'src/app/services/resources/extend.service';

@Component({
  selector: 'app-flex-renew-modal',
  templateUrl: './flex-renew-modal.component.html',
  styleUrls: ['./flex-renew-modal.component.scss']
})
export class FlexRenewModalComponent implements OnInit {

  modalName = 'FlexrenewModal';
  isLoading: boolean = false;
  isSending: boolean = false;
  currentUUID: string = '';

  @Input() isActive;
  @Input() selectedDaysIndex: number = 0;
  @Input() currentQuotaDescription;
  @Input() userBalance;
  @Input() profileId;

  @Output() isClosed = new EventEmitter<string>();
  @Output() refreshParent = new EventEmitter<boolean>();

  @ViewChild('modalContent') modalContent: ElementRef;
  @ViewChild('closeButton') closeButton: ElementRef;

  offers: any[] = [];
  quotaOffers: any[] = [{ id: null, price: 0 }];
  daysOffers: any[] = [{ id: null, price: 0 }];
  selectedQuota: any = this.quotaOffers[0];
  selectedDays: any = this.daysOffers[0];
  totalPrice: number = 0;
  step: number = 1;
  selectedQuotaIndex: number = 0;

  constructor(
    private toastrService: ToastrService,
    private authService: AuthService,
    private offersService: OffersService,
    private extendService: ExtendService
  ) {}

  ngOnInit(): void {}

  checkIfClickedInside = (event: Event) => {
    let isClickInside = this.modalContent.nativeElement.contains(event.target);
    if (!isClickInside) {
      this.closeModal();
    }
  };

  changeStatusToClosed(modalName: string) {
    this.isClosed.emit(modalName);
  }

  getOffers() {
    this.isLoading = true;
    this.offersService.getSpecific(this.profileId)
      .pipe(take(1), finalize(() => this.isLoading = false))
      .subscribe(
        res => {
          this.offers = res['data'] || [];
          this.categorizeOffers();
        },
        err => {
          console.error('Error fetching offers:', err);
        }
      );
  }

  categorizeOffers() {
    this.quotaOffers = [
      { id: null, price: 0 },
      ...this.offers
        .map(o => {
          try {
            const desc = JSON.parse(o.description);
            if (desc.type === 'flex' && desc.sub_type === 'qouta') {
              return {
                ...o,
                descriptionParsed: desc
              };
            }
          } catch (e) {
            console.error('Invalid JSON in quota offer:', o);
          }
          return null;
        })
        .filter(o => o !== null)
        .sort((a, b) => a.price - b.price)
    ];

    this.daysOffers = [
      { id: null, price: 0 },
      ...this.offers
        .map(o => {
          try {
            const desc = JSON.parse(o.description);
            if (desc.type === 'flex' && desc.sub_type === 'exp') {
              return {
                ...o,
                descriptionParsed: desc
              };
            }
          } catch (e) {
            console.error('Invalid JSON in days offer:', o);
          }
          return null;
        })
        .filter(o => o !== null)
        .sort((a, b) => a.price - b.price)
    ];

    this.selectedQuota = this.quotaOffers[0];
    this.selectedDays = this.daysOffers[0];
  }

  updateSelection(type: string, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const index = parseInt(inputElement.value, 10);

    if (type === 'quota') {
      this.selectedQuota = this.quotaOffers[index];
    } else if (type === 'days') {
      this.selectedDays = this.daysOffers[index];
    }

    this.calculateTotal();
  }

  calculateTotal() {
    this.totalPrice = (this.selectedQuota?.price || 0) + (this.selectedDays?.price || 0);
  }

  changeStep(step: number) {
    this.step = step;
  }

  async activateOffer(): Promise<void> {
    this.isLoading = true;

    let offersToActivate = [];
    if (this.selectedQuota?.id) offersToActivate.push(this.selectedQuota);
    if (this.selectedDays?.id) offersToActivate.push(this.selectedDays);

    if (offersToActivate.length === 0) {
      this.toastrService.warning("لم يتم اختيار أي عرض للتفعيل.");
      this.isLoading = false;
      return;
    }

    let userPassword = this.getUserPassword();
    if (userPassword === null) {
      this.isLoading = false;
      return;
    }

    for (let offer of offersToActivate) {
      const desc = offer.descriptionParsed;
      const offerSummary = desc.sub_type === 'qouta'
        ? `${desc.quota} ${desc.quota_unit}`
        : `${desc.exp} ${desc.exp_unit}`;

      let data = { profile_id: offer.id, current_password: userPassword };

      try {
        const response = await this.extendService.post(data).pipe(take(1)).toPromise();
        if (response['status'] === 200) {
          this.toastrService.success(`تم تفعيل العرض: ${offerSummary} بنجاح!`);
        } else {
          this.handleActivationError(response, offerSummary);
        }
      } catch (error) {
        this.toastrService.error(`فشل تفعيل العرض: ${offerSummary}. الرجاء المحاولة لاحقًا.`);
      }
    }

    this.isLoading = false;
    this.authService.userPassword = userPassword;
    this.closeModal();
    this.refreshDashboard();
  }

  getUserPassword(): string | null {
    if (!this.authService.checkIfCameFromAutoLogin()) return 'true';
    return this.authService.userPassword || prompt("الرجاء إدخال كلمة المرور لإتمام الإجراء") || null;
  }

  handleActivationError(response: any, offerSummary: string) {
    if (response['status'] === -1) {
      if (response['message'] === 'rsp_insufficient_balance') {
        this.toastrService.error(`لا يمكنك تفعيل العرض: ${offerSummary}، الرصيد غير كافي!`);
        return;
      }
      if (response['message'] === 'rsp_invalid_password') {
        this.toastrService.error("كلمة المرور غير صحيحة، الرجاء المحاولة مجددًا!");
        return;
      }
    }
    this.toastrService.error(`حدث خطأ أثناء تفعيل العرض: ${offerSummary}. يرجى المحاولة لاحقًا.`);
  }

  closeModal() {
    this.isClosed.emit(this.modalName);
    setTimeout(() => {
      this.step = 1;
      this.selectedQuota = this.quotaOffers[0];
      this.selectedDays = this.daysOffers[0];
      this.totalPrice = 0;
      this.closeButton.nativeElement.click();
      this.changeStatusToClosed(this.modalName);
      document.removeEventListener("click", this.checkIfClickedInside, true);
    }, 500);
  }

  refreshDashboard() {
    this.refreshParent.emit(true);
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }) {
    this.getOffers();
  }
}
